import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import type { Language } from "../types";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // Header
    "app.name": "AFCON Predict",

    // Landing Screen
    "landing.hero.title": "🏆 Africa Cup of Nations 2025",
    "landing.hero.subtitle":
      "Predict matches, compete with friends, and win glory!",
    "landing.stats.leagues": "Squads",
    "landing.stats.matches": "Matches",
    "match.singular": "match",
    "match.plural": "matches",
    "landing.stats.predictions": "Predictions",
    "landing.stats.users": "Players",
    "landing.stats.yourRank": "Your Rank",
    "landing.onlineUsers": "users predicting now",
    "landing.onlineUsersSubtext": "Join the action!",
    "landing.cta.createLeague": "Create or Join Squad",
    "landing.matches.title": "Upcoming Matches",

    // Match Card
    "match.live": "LIVE",
    "match.fullTime": "FT",
    "match.vs": "VS",
    "match.upcoming": "Upcoming",

    // Predictor Screen
    "predictor.title": "Make Prediction",
    "predictor.matchInfo": "Match Information",
    "predictor.predictScore": "Predict the Score",
    "predictor.mood": "Prediction Mood",
    "predictor.mood.confident": "Confident",
    "predictor.mood.nervous": "Nervous",
    "predictor.mood.bold": "Bold",
    "predictor.recent": "Recent Predictions for this Match",
    "predictor.showPreview": "Show Preview",
    "predictor.hidePreview": "Hide Preview",
    "predictor.share": "Share",
    "predictor.save": "Save",
    "predictor.saved": "Saved!",
    "predictor.success": "✓ Prediction saved successfully!",
    "predictor.youCanModify": "You can modify it",

    // Prediction Card
    "prediction.title": "Prediction",
    "prediction.yourPrediction": "My Prediction",
    "prediction.afcon": "AFCON 2025",
    "prediction.tournament": "🏆 Africa Cup of Nations 2025",
    "prediction.from": "from",

    // Leaderboard Screen
    "leaderboard.title": "Leaderboard",
    "leaderboard.globalLeague": "Global Squad",
    "leaderboard.players": "players",
    "leaderboard.inviteFriends": "Invite Friends",
    "leaderboard.copy": "Copy",
    "leaderboard.copied": "Copied!",
    "leaderboard.achievements": "Your Achievements",
    "leaderboard.topPlayers": "Top Players",
    "leaderboard.predictions": "predictions",
    "leaderboard.accuracy": "accuracy",
    "leaderboard.points": "pts",
    "leaderboard.recent": "Recent Predictions",
    "leaderboard.createLeague": "Create Private Squad",
    "leaderboard.story": "Story",
    "leaderboard.badgeGuide": "Badge Guide",
    "squad.joinSuccess": "Successfully joined the squad!",

    // Squad Creation
    "league.create.title": "Create Private Squad",
    "league.create.subtitle":
      "Create your own squad and invite friends to compete",
    "league.create.nameLabel": "Squad Name",
    "league.create.namePlaceholder": "My Awesome Squad",
    "league.create.nameHint":
      "Choose a name for your squad (max 50 characters)",
    "league.create.create": "Create Squad",
    "league.create.creating": "Creating...",
    "league.create.cancel": "Cancel",
    "league.create.success": "Squad Created!",
    "league.create.successMessage":
      "Share this code with friends to invite them to your squad",
    "league.create.inviteCode": "Invite Code",
    "league.create.done": "Done",
    "league.create.loginRequired": "You must be logged in to create a squad",

    // Squad Rename
    "league.rename.title": "Rename Squad",
    "league.rename.subtitle": "Update your squad name",
    "league.rename.nameLabel": "Squad Name",
    "league.rename.namePlaceholder": "My Awesome Squad",
    "league.rename.nameHint": "Choose a new name for your squad (max 50 characters)",
    "league.rename.renaming": "Renaming...",
    "league.rename.rename": "Rename Squad",

    // Achievements
    "achievement.expertCAF": "Expert CAF",
    "achievement.expertCAF.description": "Predict 50 matches correctly",
    "achievement.perfect": "10 Perfect",
    "achievement.perfect.description": "Get 10 exact score predictions",
    "achievement.boldMaster": "Bold Master",
    "achievement.boldMaster.description": "Make 20 predictions in Bold mood",
    "achievement.streak": "5 Day Streak",
    "achievement.streak.description": "Predict on 5 consecutive days",
    "achievement.locked": "Locked",
    "achievement.unlocked": "Unlocked",
    "achievement.viewAll": "View All Achievements",
    "achievement.badgeGuide": "Badge Guide",
    "achievement.howToEarn": "How to earn badges",

    // Share Modal
    "share.title": "Share Prediction",
    "share.subtitle": "Download and share your prediction",
    "share.download": "Download as Image",
    "share.competitionTitle": "Compete with Friends!",
    "share.competitionMessage":
      "This app was made to encourage and compete with friends. Share your predictions and see who's the best predictor!",
    "share.instagramTitle": "Share on Instagram",
    "share.instagramInstructions":
      "Download the image, then open Instagram and share it in your story or feed. Challenge your friends to make their predictions too!",
    "share.afterDownload":
      "After downloading, open Instagram and share your prediction!",

    // Story Modal
    "story.title": "Share Invite Story",
    "story.afcon": "AFCON 2025",
    "story.subtitle": "Africa Cup of Nations",
    "story.scanToJoin": "Scan to Join",
    "story.scanToJoinSquad": "Scan to Join Squad",
    "story.compete": "Compete with friends & win glory!",
    "story.competeSquad": "Join the squad and compete together!",
    "story.download": "Download Story",
    "story.close": "Close",

    // Footer
    "footer.about": "About",
    "footer.faq": "FAQ",
    "footer.privacy": "Privacy",
    "footer.contact": "Contact",
    "footer.copyright": "© 2025 AFCON Predict. Africa Cup of Nations 2025",

    // About Page
    "about.title": "About",
    "about.whatIs": "What is AFCON Predict?",
    "about.description":
      "AFCON Predict is a fun and competitive prediction game for the Africa Cup of Nations 2025. Make predictions, compete with friends, and climb the leaderboard!",
    "about.howItWorks": "How It Works",
    "about.step1":
      "Make predictions for upcoming matches by predicting the final score",
    "about.step2": "Earn points based on the accuracy of your predictions",
    "about.step3":
      "Compete with friends in squads or climb the global leaderboard",
    "about.points": "Points",
    "about.point": "Point",
    "about.pointsExact": "Exact Score Match",
    "about.pointsResult": "Correct Result (Wrong Score)",
    "about.pointsWrong": "Wrong Result",
    "about.developedBy": "This app was developed by",

    // FAQ Page
    "faq.title": "Frequently Asked Questions",
    "faq.q1": "How do I make a prediction?",
    "faq.a1":
      "Click on any upcoming match, enter your predicted score, choose your mood, and save your prediction.",
    "faq.q2": "How are points calculated?",
    "faq.a2":
      "You get 3 points for an exact score match, 1 point for predicting the correct result (but wrong score), and 0 points for a wrong result.",
    "faq.q3": "Can I change my prediction?",
    "faq.a3":
      "Yes, you can update your prediction anytime before the match starts by clicking on the match again.",
    "faq.q4": "What are squads?",
    "faq.a4":
      "Squads are private groups where you can compete with your friends. Create a squad and share the code to invite others.",
    "faq.q5": "How do I join a squad?",
    "faq.a5":
      "Ask your friend for the squad code, then use the squad switcher in the header to join using the code.",
    "faq.q6": "What happens if I miss a match?",
    "faq.a6":
      "You can only make predictions for upcoming matches. If you miss a match, you won't be able to predict it, but you can still view results and see how others predicted.",
    "faq.developedBy": "This app was developed by",

    // Privacy Page
    "privacy.title": "Privacy Policy",
    "privacy.intro": "Your Privacy Matters",
    "privacy.dataCollection": "Data Collection",
    "privacy.dataCollectionText":
      "We collect information you provide when creating an account, such as your name, email address, and optional Instagram handle. We also collect your predictions and game statistics.",
    "privacy.dataUse": "How We Use Your Data",
    "privacy.dataUseText":
      "We use your data to provide the prediction service, calculate scores and rankings, display leaderboards, and improve the app experience.",
    "privacy.dataSharing": "Data Sharing",
    "privacy.dataSharingText":
      "We do not sell your personal data. Your predictions and statistics may be visible to other users on leaderboards and in squads, but your email address is kept private.",
    "privacy.dataSecurity": "Data Security",
    "privacy.dataSecurityText":
      "We use industry-standard security measures to protect your data. All data is stored securely using Supabase.",
    "privacy.yourRights": "Your Rights",
    "privacy.yourRightsText":
      "You have the right to access, update, or delete your account and data at any time through the app settings.",
    "privacy.changes": "Changes to This Policy",
    "privacy.changesText":
      "We may update this privacy policy from time to time. We will notify users of any significant changes.",
    "privacy.developedBy": "This app was developed by",

    // Contact Page
    "contact.title": "Contact Us",
    "contact.getInTouch": "Get in Touch",
    "contact.description":
      "Have a question, suggestion, or found a bug? We'd love to hear from you!",
    "contact.email": "Your Email",
    "contact.emailPlaceholder": "you@example.com",
    "contact.message": "Message",
    "contact.messagePlaceholder": "Tell us what's on your mind...",
    "contact.send": "Send Message",
    "contact.success": "Message Sent!",
    "contact.successMessage":
      "Thank you for contacting us. We'll get back to you soon.",
    "contact.alternative": "Alternative Contact",
    "contact.alternativeText": "You can also reach us directly at:",
    "contact.developedBy": "This app was developed by",

    // Time
    "time.today": "Today",
    "time.tomorrow": "Tomorrow",
    "time.yesterday": "Yesterday",
    "time.sunday": "Sunday",
    "time.monday": "Monday",
    "time.tuesday": "Tuesday",
    "time.wednesday": "Wednesday",
    "time.thursday": "Thursday",
    "time.friday": "Friday",
    "time.saturday": "Saturday",
    "time.january": "January",
    "time.february": "February",
    "time.march": "March",
    "time.april": "April",
    "time.may": "May",
    "time.june": "June",
    "time.july": "July",
    "time.august": "August",
    "time.september": "September",
    "time.october": "October",
    "time.november": "November",
    "time.december": "December",
    "time.justNow": "just now",
    "time.fewSecondsAgo": "few seconds ago",
    "time.minute": "min",
    "time.minutes": "min",
    "time.hour": "hour",
    "time.hours": "hours",
    "time.day": "day",
    "time.days": "days",
    "time.ago": "ago",
    "time.jan": "Jan",
    "time.feb": "Feb",
    "time.mar": "Mar",
    "time.apr": "Apr",
    "time.jun": "Jun",
    "time.jul": "Jul",
    "time.aug": "Aug",
    "time.sep": "Sep",
    "time.oct": "Oct",
    "time.nov": "Nov",
    "time.dec": "Dec",

    // Login Modal
    "login.title": "Welcome Back",
    "login.subtitle": "Sign in to your account",
    "login.email": "Email",
    "login.password": "Password",
    "login.name": "Name",
    "login.namePlaceholder": "John Doe",
    "login.signIn": "Sign In",
    "login.signUp": "Sign Up",
    "login.noAccount": "Don't have an account?",
    "login.hasAccount": "Already have an account?",
    "login.createAccount": "Create Account",
    "login.logout": "Logout",
    "login.profile": "Profile",
    "login.instagram": "Instagram (Optional)",
    "login.instagramHint":
      "Your handle will be featured in our daily Instagram stories!",
    "login.supportedCountry": "Team You Support",
    "login.selectCountry": "Select your team",

    // Common
    "common.loading": "Loading...",
    "common.saving": "Saving...",
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.back": "Back",
    "common.noPredictions": "No predictions yet",
    "common.noPlayers": "No players yet",
    "common.signInToSave": "Sign in to save",
    "common.predictionSaved": "Prediction saved",
    "common.noPredictionsMatch": "No predictions yet for this match",
    "common.possessive": "'s",
    "common.your": "Your",
    "common.nameRequired": "Name is required",
    "common.failedCreateAccount": "Failed to create account",
    "common.unexpectedError": "An unexpected error occurred",
    "common.loginRequired": "You must be logged in to create a squad",
    "common.leagueNameRequired": "Squad name is required",
    "common.failedCreateLeague": "Failed to create squad",
    "common.failedRenameLeague": "Failed to rename squad",
    "common.downloadFailed": "Download failed. Please try again.",
    "common.errorTitle": "Something went wrong",
    "common.errorMessage":
      "We're sorry, but something unexpected happened. Please try refreshing the page.",
    "common.refreshPage": "Refresh Page",
    "common.error": "Error",
    "common.downloadSuccess": "Image downloaded successfully!",
    "common.maybeLater": "Maybe Later",
    "common.notNow": "Not Now",
    "common.change": "Change Prediction",
    "common.profileUpdated": "Profile updated successfully!",

    // First Time Prediction Modal
    "firstTime.welcome": "Welcome! 🎉",
    "firstTime.subtitle":
      "Make your first prediction and join the competition!",
    "firstTime.whoWins": "Who do you think will win?",
    "firstTime.wins": "Wins",
    "firstTime.draw": "Draw",
    "firstTime.continue": "Continue",
    "firstTime.yourPrediction": "Your Prediction",
    "firstTime.signUpToSave": "Ready to Join? 🎉",
    "firstTime.signUpPrompt":
      "Great prediction! Create a free account to save it and compete with others. It only takes a moment!",
    "firstTime.saveYourPrediction": "Save Your Prediction",
    "firstTime.signUpDescription":
      "Create an account to save your prediction, track your points, and compete on the leaderboard!",
    "firstTime.signUpNow": "Sign Up Now",
    "firstTime.predictionSaved": "Your prediction has been saved!",

    // First Prediction Info Modal
    "firstPrediction.title": "Complete Your Profile",
    "firstPrediction.subtitle":
      "Please provide your information to continue with your first prediction",
    "firstPrediction.continue": "Continue",

    // Login/Signup
    "login.success": "Successfully signed in!",
    "login.signupSuccess": "Account created successfully!",

    // Squad Operations
    "squad.createSuccess": "Squad created successfully!",
    "squad.renameSuccess": "Squad renamed successfully!",
    "squad.leaveSuccess": "Left squad successfully!",

    // Admin Screen
    "admin.title": "Admin",
    "admin.panel": "Admin Panel",
    "admin.matches": "Matches",
    "admin.users": "Users",
    "admin.squads": "Squads",
    "admin.allUsers": "All Users",
    "admin.allSquads": "All Squads",
    "admin.noUsers": "No users found",
    "admin.noSquads": "No squads found",
    "admin.predictions": "Predictions",
    "admin.members": "Members",
    "admin.registered": "Registered",
    "admin.created": "Created",
    "admin.createdBy": "Created by",
    "admin.squad": "Squad",
    "admin.squadNotFound": "Squad not found",
    "admin.deleteSquad": "Delete Squad",
    "admin.confirmDeleteSquad": "Are you sure you want to delete the squad",
    "admin.deleteSquadWarning": "This action cannot be undone.",
    "admin.squadDeleted": "Squad deleted successfully",
    "admin.failedToDeleteSquad": "Failed to delete squad",
    "admin.cannotDeleteSquadWithMembers": "Cannot delete squad with members. Remove all members first.",
    "admin.activeMatches": "Active Matches",
    "admin.noActiveMatches": "No active matches",
    "admin.completedMatches": "Completed Matches",
    "admin.noCompletedMatches": "No completed matches yet",
    "admin.matchUpdated": "Match updated successfully",
    "admin.matchCompleted":
      "Match completed! Points awarded and ranks updated.",
    "admin.status.upcoming": "Upcoming",
    "admin.status.live": "Live",
    "admin.status.completed": "Completed",
    "admin.save": "Save",
    "admin.saving": "Saving",
    "admin.errorDetails": "Error Details",

    // Landing Screen
    "landing.noMatches": "No upcoming matches available",

    // Squad
    "league.global": "Global",
    "league.myLeague": "My Squad",
    "league.create": "Create Squad",
    "league.renameShare": "Rename/Share",
    "league.join": "Join Squad",
    "league.leave": "Leave Squad",
    "league.code": "Code",
    "league.members": "Members",
    "league.confirmLeave": "Are you sure you want to leave this squad?",
    "league.namePlaceholder": "Enter squad name",
    "league.codePlaceholder": "Enter 6-digit code",

    // Podium Modal
    "podium.title": "🏆 Current Leaders",
    "podium.subtitle": "See who's leading the competition!",
    "podium.cta": "Want to join the podium?",
    "podium.description":
      "Make predictions for upcoming matches and climb the leaderboard! Your name could be here too!",
    "podium.startPredicting": "Start Predicting",
  },

  fr: {
    // Header
    "app.name": "AFCON Predict",

    // Landing Screen
    "landing.hero.title": "🏆 Coupe d'Afrique des Nations 2025",
    "landing.hero.subtitle":
      "Prédisez les matchs, affrontez vos amis et gagnez la gloire!",
    "landing.stats.leagues": "Équipes",
    "landing.stats.matches": "Matchs",
    "match.singular": "match",
    "match.plural": "matchs",
    "landing.stats.predictions": "Prédictions",
    "landing.stats.users": "Joueurs",
    "landing.stats.yourRank": "Votre Rang",
    "landing.onlineUsers": "utilisateurs prédisent maintenant",
    "landing.onlineUsersSubtext": "Rejoignez l'action !",
    "landing.cta.createLeague": "Créer ou Rejoindre une Équipe",
    "landing.matches.title": "Matchs à Venir",

    // Match Card
    "match.live": "EN DIRECT",
    "match.fullTime": "FT",
    "match.vs": "VS",
    "match.upcoming": "À venir",

    // Predictor Screen
    "predictor.title": "Faire une Prédiction",
    "predictor.matchInfo": "Information du Match",
    "predictor.predictScore": "Prédire le Score",
    "predictor.mood": "Humeur de Prédiction",
    "predictor.mood.confident": "Confiant",
    "predictor.mood.nervous": "Nerveux",
    "predictor.mood.bold": "Audacieux",
    "predictor.recent": "Prédictions Récentes pour ce Match",
    "predictor.showPreview": "Afficher l'Aperçu",
    "predictor.hidePreview": "Masquer l'Aperçu",
    "predictor.share": "Partager",
    "predictor.save": "Enregistrer",
    "predictor.saved": "Enregistré!",
    "predictor.success": "✓ Prédiction enregistrée avec succès!",
    "predictor.youCanModify": "Vous pouvez la modifier",

    // Prediction Card
    "prediction.title": "Prédiction",
    "prediction.yourPrediction": "Ma Prédiction",
    "prediction.afcon": "CAN 2025",
    "prediction.tournament": "🏆 Coupe d'Afrique des Nations 2025",
    "prediction.from": "de",

    // Leaderboard Screen
    "leaderboard.title": "Classement",
    "leaderboard.globalLeague": "Équipe Mondiale",
    "leaderboard.players": "joueurs",
    "leaderboard.inviteFriends": "Inviter des Amis",
    "leaderboard.copy": "Copier",
    "leaderboard.copied": "Copié!",
    "leaderboard.achievements": "Vos Réalisations",
    "leaderboard.topPlayers": "Meilleurs Joueurs",
    "leaderboard.predictions": "prédictions",
    "leaderboard.accuracy": "précision",
    "leaderboard.points": "pts",
    "leaderboard.recent": "Prédictions Récentes",
    "leaderboard.createLeague": "Créer une Équipe Privée",
    "leaderboard.story": "Histoire",
    "leaderboard.badgeGuide": "Guide des Badges",
    "squad.joinSuccess": "Vous avez rejoint l'équipe avec succès !",

    // Squad Creation
    "league.create.title": "Créer une Équipe Privée",
    "league.create.subtitle":
      "Créez votre propre équipe et invitez vos amis à participer",
    "league.create.nameLabel": "Nom de l'Équipe",
    "league.create.namePlaceholder": "Mon Équipe Géniale",
    "league.create.nameHint":
      "Choisissez un nom pour votre équipe (max 50 caractères)",
    "league.create.create": "Créer l'Équipe",
    "league.create.creating": "Création...",
    "league.create.cancel": "Annuler",
    "league.create.success": "Équipe Créée!",
    "league.create.successMessage":
      "Partagez ce code avec vos amis pour les inviter à votre équipe",
    "league.create.inviteCode": "Code d'Invitation",
    "league.create.done": "Terminé",
    "league.create.loginRequired":
      "Vous devez être connecté pour créer une équipe",

    // Squad Rename
    "league.rename.title": "Renommer l'Équipe",
    "league.rename.subtitle": "Mettez à jour le nom de votre équipe",
    "league.rename.nameLabel": "Nom de l'Équipe",
    "league.rename.namePlaceholder": "Mon Équipe Géniale",
    "league.rename.nameHint": "Choisissez un nouveau nom pour votre équipe (max 50 caractères)",
    "league.rename.renaming": "Renommage...",
    "league.rename.rename": "Renommer l'Équipe",

    // Achievements
    "achievement.expertCAF": "Expert CAF",
    "achievement.expertCAF.description": "Prédire 50 matchs correctement",
    "achievement.perfect": "10 Parfait",
    "achievement.perfect.description": "Obtenir 10 prédictions de score exact",
    "achievement.boldMaster": "Maître Audacieux",
    "achievement.boldMaster.description":
      "Faire 20 prédictions en mode Audacieux",
    "achievement.streak": "Série de 5 Jours",
    "achievement.streak.description": "Prédire pendant 5 jours consécutifs",
    "achievement.locked": "Verrouillé",
    "achievement.unlocked": "Déverrouillé",
    "achievement.viewAll": "Voir Tous les Succès",
    "achievement.badgeGuide": "Guide des Badges",
    "achievement.howToEarn": "Comment gagner des badges",

    // Share Modal
    "share.title": "Partager la Prédiction",
    "share.subtitle": "Téléchargez et partagez votre prédiction",
    "share.download": "Télécharger comme Image",
    "share.competitionTitle": "Rivalisez avec vos Amis !",
    "share.competitionMessage":
      "Cette application a été créée pour encourager et rivaliser avec vos amis. Partagez vos prédictions et voyez qui est le meilleur prédicteur !",
    "share.instagramTitle": "Partager sur Instagram",
    "share.instagramInstructions":
      "Téléchargez l'image, puis ouvrez Instagram et partagez-la dans votre story ou votre feed. Défiez vos amis à faire leurs prédictions aussi !",
    "share.afterDownload":
      "Après le téléchargement, ouvrez Instagram et partagez votre prédiction !",

    // Story Modal
    "story.title": "Partager l'Histoire d'Invitation",
    "story.afcon": "CAN 2025",
    "story.subtitle": "Coupe d'Afrique des Nations",
    "story.scanToJoin": "Scanner pour Rejoindre",
    "story.scanToJoinSquad": "Scanner pour Rejoindre l'Équipe",
    "story.compete": "Affronter vos amis et gagner la gloire!",
    "story.competeSquad": "Rejoignez l'équipe et affrontez-vous ensemble!",
    "story.download": "Télécharger l'Histoire",
    "story.close": "Fermer",

    // Footer
    "footer.about": "À Propos",
    "footer.faq": "FAQ",
    "footer.privacy": "Confidentialité",
    "footer.contact": "Contact",
    "footer.copyright":
      "© 2025 AFCON Predict. Coupe d'Afrique des Nations 2025",

    // About Page
    "about.title": "À Propos",
    "about.whatIs": "Qu'est-ce que AFCON Predict?",
    "about.description":
      "AFCON Predict est un jeu de prédiction amusant et compétitif pour la Coupe d'Afrique des Nations 2025. Faites des prédictions, rivalisez avec vos amis et grimpez au classement!",
    "about.howItWorks": "Comment ça marche",
    "about.step1":
      "Faites des prédictions pour les matchs à venir en prédisant le score final",
    "about.step2":
      "Gagnez des points en fonction de la précision de vos prédictions",
    "about.step3":
      "Rivalisez avec vos amis dans des équipes ou grimpez au classement mondial",
    "about.points": "Points",
    "about.point": "Point",
    "about.pointsExact": "Score Exact",
    "about.pointsResult": "Résultat Correct (Mauvais Score)",
    "about.pointsWrong": "Mauvais Résultat",
    "about.developedBy": "Cette application a été développée par",

    // FAQ Page
    "faq.title": "Questions Fréquemment Posées",
    "faq.q1": "Comment faire une prédiction?",
    "faq.a1":
      "Cliquez sur n'importe quel match à venir, entrez votre score prédit, choisissez votre humeur et enregistrez votre prédiction.",
    "faq.q2": "Comment les points sont-ils calculés?",
    "faq.a2":
      "Vous obtenez 3 points pour un score exact, 1 point pour prédire le bon résultat (mais mauvais score), et 0 point pour un mauvais résultat.",
    "faq.q3": "Puis-je changer ma prédiction?",
    "faq.a3":
      "Oui, vous pouvez mettre à jour votre prédiction à tout moment avant le début du match en cliquant à nouveau sur le match.",
    "faq.q4": "Que sont les équipes?",
    "faq.a4":
      "Les équipes sont des groupes privés où vous pouvez rivaliser avec vos amis. Créez une équipe et partagez le code pour inviter d'autres personnes.",
    "faq.q5": "Comment rejoindre une équipe?",
    "faq.a5":
      "Demandez le code de l'équipe à votre ami, puis utilisez le sélecteur d'équipe dans l'en-tête pour rejoindre en utilisant le code.",
    "faq.q6": "Que se passe-t-il si je rate un match?",
    "faq.a6":
      "Vous ne pouvez faire des prédictions que pour les matchs à venir. Si vous ratez un match, vous ne pourrez pas le prédire, mais vous pourrez toujours voir les résultats et voir comment les autres ont prédit.",
    "faq.developedBy": "Cette application a été développée par",

    // Privacy Page
    "privacy.title": "Politique de Confidentialité",
    "privacy.intro": "Votre Confidentialité Compte",
    "privacy.dataCollection": "Collecte de Données",
    "privacy.dataCollectionText":
      "Nous collectons les informations que vous fournissez lors de la création d'un compte, telles que votre nom, votre adresse e-mail et votre identifiant Instagram optionnel. Nous collectons également vos prédictions et statistiques de jeu.",
    "privacy.dataUse": "Comment Nous Utilisons Vos Données",
    "privacy.dataUseText":
      "Nous utilisons vos données pour fournir le service de prédiction, calculer les scores et classements, afficher les classements et améliorer l'expérience de l'application.",
    "privacy.dataSharing": "Partage de Données",
    "privacy.dataSharingText":
      "Nous ne vendons pas vos données personnelles. Vos prédictions et statistiques peuvent être visibles par d'autres utilisateurs sur les classements et dans les équipes, mais votre adresse e-mail reste privée.",
    "privacy.dataSecurity": "Sécurité des Données",
    "privacy.dataSecurityText":
      "Nous utilisons des mesures de sécurité standard de l'industrie pour protéger vos données. Toutes les données sont stockées en toute sécurité à l'aide de Supabase.",
    "privacy.yourRights": "Vos Droits",
    "privacy.yourRightsText":
      "Vous avez le droit d'accéder, de mettre à jour ou de supprimer votre compte et vos données à tout moment via les paramètres de l'application.",
    "privacy.changes": "Modifications de Cette Politique",
    "privacy.changesText":
      "Nous pouvons mettre à jour cette politique de confidentialité de temps à autre. Nous informerons les utilisateurs de tout changement important.",
    "privacy.developedBy": "Cette application a été développée par",

    // Contact Page
    "contact.title": "Contactez-Nous",
    "contact.getInTouch": "Entrer en Contact",
    "contact.description":
      "Vous avez une question, une suggestion ou avez trouvé un bug? Nous aimerions avoir de vos nouvelles!",
    "contact.email": "Votre Email",
    "contact.emailPlaceholder": "vous@exemple.com",
    "contact.message": "Message",
    "contact.messagePlaceholder": "Dites-nous ce qui vous préoccupe...",
    "contact.send": "Envoyer le Message",
    "contact.success": "Message Envoyé!",
    "contact.successMessage":
      "Merci de nous avoir contactés. Nous vous répondrons bientôt.",
    "contact.alternative": "Contact Alternatif",
    "contact.alternativeText":
      "Vous pouvez également nous contacter directement à:",
    "contact.developedBy": "Cette application a été développée par",

    // Time
    "time.today": "Aujourd'hui",
    "time.tomorrow": "Demain",
    "time.yesterday": "Hier",
    "time.sunday": "Dimanche",
    "time.monday": "Lundi",
    "time.tuesday": "Mardi",
    "time.wednesday": "Mercredi",
    "time.thursday": "Jeudi",
    "time.friday": "Vendredi",
    "time.saturday": "Samedi",
    "time.january": "Janvier",
    "time.february": "Février",
    "time.march": "Mars",
    "time.april": "Avril",
    "time.may": "Mai",
    "time.june": "Juin",
    "time.july": "Juillet",
    "time.august": "Août",
    "time.september": "Septembre",
    "time.october": "Octobre",
    "time.november": "Novembre",
    "time.december": "Décembre",
    "time.justNow": "à l'instant",
    "time.fewSecondsAgo": "il y a quelques secondes",
    "time.minute": "min",
    "time.minutes": "min",
    "time.hour": "heure",
    "time.hours": "heures",
    "time.day": "jour",
    "time.days": "jours",
    "time.ago": "il y a",
    "time.jan": "Jan",
    "time.feb": "Fév",
    "time.mar": "Mar",
    "time.apr": "Avr",
    "time.jun": "Juin",
    "time.jul": "Juil",
    "time.aug": "Août",
    "time.sep": "Sep",
    "time.oct": "Oct",
    "time.nov": "Nov",
    "time.dec": "Déc",

    // Login Modal
    "login.title": "Bienvenue à nouveau",
    "login.subtitle": "Connectez-vous à votre compte",
    "login.email": "Email",
    "login.password": "Mot de passe",
    "login.name": "Nom",
    "login.namePlaceholder": "Jean Dupont",
    "login.signIn": "Se connecter",
    "login.signUp": "S'inscrire",
    "login.noAccount": "Vous n'avez pas de compte ?",
    "login.hasAccount": "Vous avez déjà un compte ?",
    "login.createAccount": "Créer un compte",
    "login.logout": "Déconnexion",
    "login.profile": "Profil",
    "login.instagram": "Instagram (Optionnel)",
    "login.instagramHint":
      "Votre nom sera mis en avant dans nos stories Instagram quotidiennes !",
    "login.supportedCountry": "Équipe que vous soutenez",
    "login.selectCountry": "Sélectionnez votre équipe",

    // Common
    "common.loading": "Chargement...",
    "common.saving": "Enregistrement...",
    "common.cancel": "Annuler",
    "common.close": "Fermer",
    "common.back": "Retour",
    "common.noPredictions": "Pas encore de prédictions",
    "common.noPlayers": "Pas encore de joueurs",
    "common.signInToSave": "Connectez-vous pour enregistrer",
    "common.predictionSaved": "Prédiction enregistrée",
    "common.noPredictionsMatch": "Pas encore de prédictions pour ce match",
    "common.possessive": " de",
    "common.your": "Votre",
    "common.nameRequired": "Le nom est obligatoire",
    "common.failedCreateAccount": "Échec de la création du compte",
    "common.unexpectedError": "Une erreur inattendue s'est produite",
    "common.loginRequired": "Vous devez être connecté pour créer une équipe",
    "common.leagueNameRequired": "Le nom de l'équipe est obligatoire",
    "common.failedCreateLeague": "Échec de la création de l'équipe",
    "common.failedRenameLeague": "Échec du renommage de l'équipe",
    "common.downloadFailed": "Échec du téléchargement. Veuillez réessayer.",
    "common.errorTitle": "Quelque chose s'est mal passé",
    "common.errorMessage":
      "Nous sommes désolés, mais quelque chose d'inattendu s'est produit. Veuillez actualiser la page.",
    "common.refreshPage": "Actualiser la page",
    "common.error": "Erreur",
    "common.downloadSuccess": "Image téléchargée avec succès !",
    "common.maybeLater": "Peut-être plus tard",
    "common.notNow": "Pas maintenant",
    "common.change": "Modifier la prédiction",
    "common.profileUpdated": "Profil mis à jour avec succès !",

    // Login/Signup
    "login.success": "Connexion réussie !",
    "login.signupSuccess": "Compte créé avec succès !",

    // First Time Prediction Modal
    "firstTime.welcome": "Bienvenue ! 🎉",
    "firstTime.subtitle":
      "Faites votre première prédiction et rejoignez la compétition !",
    "firstTime.whoWins": "Qui pensez-vous gagnera ?",
    "firstTime.wins": "Gagne",
    "firstTime.draw": "Match Nul",
    "firstTime.continue": "Continuer",
    "firstTime.yourPrediction": "Votre Prédiction",
    "firstTime.signUpToSave": "Prêt à Rejoindre ? 🎉",
    "firstTime.signUpPrompt":
      "Excellente prédiction ! Créez un compte gratuit pour l'enregistrer et rivaliser avec les autres. Cela ne prend qu'un instant !",
    "firstTime.saveYourPrediction": "Enregistrez Votre Prédiction",
    "firstTime.signUpDescription":
      "Créez un compte pour enregistrer votre prédiction, suivre vos points et participer au classement !",
    "firstTime.signUpNow": "S'inscrire Maintenant",
    "firstTime.predictionSaved": "Votre prédiction a été enregistrée !",

    // First Prediction Info Modal
    "firstPrediction.title": "Complétez votre profil",
    "firstPrediction.subtitle":
      "Veuillez fournir vos informations pour continuer avec votre première prédiction",
    "firstPrediction.continue": "Continuer",

    // Squad Operations
    "squad.createSuccess": "Équipe créée avec succès !",
    "squad.renameSuccess": "Équipe renommée avec succès !",
    "squad.leaveSuccess": "Équipe quittée avec succès !",

    // Admin Screen
    "admin.title": "Admin",
    "admin.panel": "Panneau d'administration",
    "admin.matches": "Matchs",
    "admin.users": "Utilisateurs",
    "admin.squads": "Équipes",
    "admin.allUsers": "Tous les Utilisateurs",
    "admin.allSquads": "Toutes les Équipes",
    "admin.noUsers": "Aucun utilisateur trouvé",
    "admin.noSquads": "Aucune équipe trouvée",
    "admin.predictions": "Prédictions",
    "admin.members": "Membres",
    "admin.registered": "Inscrit",
    "admin.created": "Créé",
    "admin.createdBy": "Créé par",
    "admin.squad": "Équipe",
    "admin.squadNotFound": "Équipe introuvable",
    "admin.deleteSquad": "Supprimer l'équipe",
    "admin.confirmDeleteSquad": "Êtes-vous sûr de vouloir supprimer l'équipe",
    "admin.deleteSquadWarning": "Cette action ne peut pas être annulée.",
    "admin.squadDeleted": "Équipe supprimée avec succès",
    "admin.failedToDeleteSquad": "Échec de la suppression de l'équipe",
    "admin.cannotDeleteSquadWithMembers": "Impossible de supprimer une équipe avec des membres. Supprimez d'abord tous les membres.",
    "admin.activeMatches": "Matchs Actifs",
    "admin.noActiveMatches": "Aucun match actif",
    "admin.completedMatches": "Matchs Terminés",
    "admin.noCompletedMatches": "Aucun match terminé pour le moment",
    "admin.matchUpdated": "Match mis à jour avec succès",
    "admin.matchCompleted":
      "Match terminé ! Points attribués et classements mis à jour.",
    "admin.status.upcoming": "À Venir",
    "admin.status.live": "En Direct",
    "admin.status.completed": "Terminé",
    "admin.save": "Enregistrer",
    "admin.saving": "Enregistrement...",
    "admin.errorDetails": "Détails de l'erreur",

    // Landing Screen
    "landing.noMatches": "Aucun match à venir disponible",

    // Squad
    "league.global": "Mondial",
    "league.myLeague": "Mon Équipe",
    "league.create": "Créer une Équipe",
    "league.renameShare": "Renommer/Partager",
    "league.join": "Rejoindre une Équipe",
    "league.leave": "Quitter l'Équipe",
    "league.code": "Code",
    "league.members": "Membres",
    "league.confirmLeave": "Êtes-vous sûr de vouloir quitter cette équipe ?",
    "league.namePlaceholder": "Entrez le nom de l'équipe",
    "league.codePlaceholder": "Entrez le code à 6 chiffres",

    // Podium Modal
    "podium.title": "🏆 Leaders Actuels",
    "podium.subtitle": "Découvrez qui mène la compétition !",
    "podium.cta": "Vous voulez rejoindre le podium ?",
    "podium.description":
      "Faites des prédictions pour les matchs à venir et grimpez au classement ! Votre nom pourrait aussi être là !",
    "podium.startPredicting": "Commencer à Prédire",
  },

  ar: {
    // Header
    "app.name": "AFCON Predict",

    // Landing Screen
    "landing.hero.title": "🏆 كأس أمم أفريقيا 2025",
    "landing.hero.subtitle": "توقع المباريات، تنافس مع الأصدقاء، واربح المجد!",
    "landing.stats.leagues": "فرق",
    "landing.stats.matches": "مباريات",
    "match.singular": "مباراة",
    "match.plural": "مباريات",
    "landing.stats.predictions": "توقعات",
    "landing.stats.users": "لاعبون",
    "landing.stats.yourRank": "ترتيبك",
    "landing.onlineUsers": "مستخدم يتوقعون الآن",
    "landing.onlineUsersSubtext": "انضم إلى الحركة!",
    "landing.cta.createLeague": "إنشاء أو الانضمام إلى فريق",
    "landing.matches.title": "المباريات القادمة",

    // Match Card
    "match.live": "مباشر",
    "match.fullTime": "انتهت",
    "match.vs": "ضد",
    "match.upcoming": "قادمة",

    // Predictor Screen
    "predictor.title": "إجراء توقع",
    "predictor.matchInfo": "معلومات المباراة",
    "predictor.predictScore": "توقع النتيجة",
    "predictor.mood": "مزاج التوقع",
    "predictor.mood.confident": "واثق",
    "predictor.mood.nervous": "متوتر",
    "predictor.mood.bold": "جريء",
    "predictor.recent": "التوقعات الأخيرة لهذه المباراة",
    "predictor.showPreview": "إظهار المعاينة",
    "predictor.hidePreview": "إخفاء المعاينة",
    "predictor.share": "مشاركة",
    "predictor.save": "حفظ",
    "predictor.saved": "تم الحفظ!",
    "predictor.success": "✓ تم حفظ التوقع بنجاح!",
    "predictor.youCanModify": "يمكنك تعديله",

    // Prediction Card
    "prediction.title": "توقع",
    "prediction.yourPrediction": "توقعي",
    "prediction.afcon": "كأس أمم أفريقيا 2025",
    "prediction.tournament": "🏆 كأس أمم أفريقيا 2025",
    "prediction.from": "من",

    // Leaderboard Screen
    "leaderboard.title": "لوحة المتصدرين",
    "leaderboard.globalLeague": "الفريق العالمي",
    "leaderboard.players": "لاعبين",
    "leaderboard.inviteFriends": "دعوة الأصدقاء",
    "leaderboard.copy": "نسخ",
    "leaderboard.copied": "تم النسخ!",
    "leaderboard.achievements": "إنجازاتك",
    "leaderboard.topPlayers": "أفضل اللاعبين",
    "leaderboard.predictions": "توقعات",
    "leaderboard.accuracy": "دقة",
    "leaderboard.points": "نقاط",
    "leaderboard.recent": "التوقعات الأخيرة",
    "leaderboard.createLeague": "إنشاء فريق خاص",
    "leaderboard.story": "قصة",
    "leaderboard.badgeGuide": "دليل الشارات",
    "squad.joinSuccess": "تم الانضمام إلى الفريق بنجاح!",

    // Squad Creation
    "league.create.title": "إنشاء فريق خاص",
    "league.create.subtitle": "أنشئ فريقك الخاص وادعُ أصدقاءك للمنافسة",
    "league.create.nameLabel": "اسم الفريق",
    "league.create.namePlaceholder": "فريقي الرائع",
    "league.create.nameHint": "اختر اسماً لفريقك (حد أقصى 50 حرفاً)",
    "league.create.create": "إنشاء الفريق",
    "league.create.creating": "جاري الإنشاء...",
    "league.create.cancel": "إلغاء",
    "league.create.success": "تم إنشاء الفريق!",
    "league.create.successMessage":
      "شارك هذا الرمز مع أصدقائك لدعوتهم إلى فريقك",
    "league.create.inviteCode": "رمز الدعوة",
    "league.create.done": "تم",
    "league.create.loginRequired": "يجب تسجيل الدخول لإنشاء فريق",

    // Squad Rename
    "league.rename.title": "إعادة تسمية الفريق",
    "league.rename.subtitle": "قم بتحديث اسم فريقك",
    "league.rename.nameLabel": "اسم الفريق",
    "league.rename.namePlaceholder": "فريقي الرائع",
    "league.rename.nameHint": "اختر اسماً جديداً لفريقك (حد أقصى 50 حرفاً)",
    "league.rename.renaming": "جاري إعادة التسمية...",
    "league.rename.rename": "إعادة تسمية الفريق",

    // Achievements
    "achievement.expertCAF": "خبير كأس أفريقيا",
    "achievement.expertCAF.description": "توقع 50 مباراة بشكل صحيح",
    "achievement.perfect": "10 مثالي",
    "achievement.perfect.description": "احصل على 10 توقعات دقيقة للنتيجة",
    "achievement.boldMaster": "سيد الجرأة",
    "achievement.boldMaster.description": "قدم 20 توقعًا في وضع الجريء",
    "achievement.streak": "سلسلة 5 أيام",
    "achievement.streak.description": "توقع لمدة 5 أيام متتالية",
    "achievement.locked": "مقفل",
    "achievement.unlocked": "مفتوح",
    "achievement.viewAll": "عرض جميع الإنجازات",
    "achievement.badgeGuide": "دليل الشارات",
    "achievement.howToEarn": "كيفية الحصول على الشارات",

    // Share Modal
    "share.title": "مشاركة التوقع",
    "share.subtitle": "قم بتنزيل ومشاركة توقعك",
    "share.download": "تحميل كصورة",
    "share.competitionTitle": "تنافس مع أصدقائك!",
    "share.competitionMessage":
      "تم إنشاء هذا التطبيق لتشجيع والتنافس مع الأصدقاء. شارك توقعاتك وانظر من هو أفضل متنبئ!",
    "share.instagramTitle": "شارك على إنستغرام",
    "share.instagramInstructions":
      "قم بتنزيل الصورة، ثم افتح إنستغرام وشاركها في قصتك أو منشورك. تحدّ أصدقاءك ليقوموا بتوقعاتهم أيضاً!",
    "share.afterDownload": "بعد التنزيل، افتح إنستغرام وشارك توقعك!",

    // Story Modal
    "story.title": "مشاركة قصص الدعوة",
    "story.afcon": "كأس أمم أفريقيا 2025",
    "story.subtitle": "كأس أمم أفريقيا",
    "story.scanToJoin": "مسح للانضمام",
    "story.scanToJoinSquad": "مسح للانضمام إلى الفريق",
    "story.compete": "تنافس مع الأصدقاء واربح المجد!",
    "story.competeSquad": "انضم إلى الفريق وتنافسوا معاً!",
    "story.download": "تحميل القصة",
    "story.close": "إغلاق",

    // Footer
    "footer.about": "حول",
    "footer.faq": "الأسئلة الشائعة",
    "footer.privacy": "الخصوصية",
    "footer.contact": "اتصل بنا",
    "footer.copyright": "© 2025 AFCON Predict. كأس أمم أفريقيا 2025",

    // About Page
    "about.title": "حول",
    "about.whatIs": "ما هو AFCON Predict?",
    "about.description":
      "AFCON Predict هو لعبة توقع ممتعة وتنافسية لكأس أمم أفريقيا 2025. قم بعمل توقعات، تنافس مع أصدقائك، وصعد في الترتيب!",
    "about.howItWorks": "كيف يعمل",
    "about.step1":
      "قم بعمل توقعات للمباريات القادمة من خلال توقع النتيجة النهائية",
    "about.step2": "اكسب نقاط بناءً على دقة توقعاتك",
    "about.step3": "تنافس مع أصدقائك في الفرق أو صعد في الترتيب العالمي",
    "about.points": "النقاط",
    "about.point": "نقطة",
    "about.pointsExact": "نتيجة صحيحة بالضبط",
    "about.pointsResult": "نتيجة صحيحة (نتيجة خاطئة)",
    "about.pointsWrong": "نتيجة خاطئة",
    "about.developedBy": "تم تطوير هذا التطبيق بواسطة",

    // FAQ Page
    "faq.title": "الأسئلة الشائعة",
    "faq.q1": "كيف أقوم بعمل توقع?",
    "faq.a1":
      "انقر على أي مباراة قادمة، أدخل النتيجة المتوقعة، اختر حالتك المزاجية، واحفظ توقعك.",
    "faq.q2": "كيف يتم حساب النقاط?",
    "faq.a2":
      "تحصل على 3 نقاط للنتيجة الصحيحة بالضبط، نقطة واحدة لتوقع النتيجة الصحيحة (لكن النتيجة خاطئة)، و 0 نقطة للنتيجة الخاطئة.",
    "faq.q3": "هل يمكنني تغيير توقعي?",
    "faq.a3":
      "نعم، يمكنك تحديث توقعك في أي وقت قبل بدء المباراة بالنقر على المباراة مرة أخرى.",
    "faq.q4": "ما هي الفرق?",
    "faq.a4":
      "الفرق هي مجموعات خاصة حيث يمكنك التنافس مع أصدقائك. أنشئ فريقًا وشارك الرمز لدعوة الآخرين.",
    "faq.q5": "كيف أنضم إلى فريق?",
    "faq.a5":
      "اطلب من صديقك رمز الفريق، ثم استخدم محول الفريق في الرأس للانضمام باستخدام الرمز.",
    "faq.q6": "ماذا يحدث إذا فاتتني مباراة?",
    "faq.a6":
      "يمكنك فقط عمل توقعات للمباريات القادمة. إذا فاتتك مباراة، لن تتمكن من توقعها، لكن يمكنك仍然 عرض النتائج ومعرفة كيف توقع الآخرون.",
    "faq.developedBy": "تم تطوير هذا التطبيق بواسطة",

    // Privacy Page
    "privacy.title": "سياسة الخصوصية",
    "privacy.intro": "خصوصيتك مهمة",
    "privacy.dataCollection": "جمع البيانات",
    "privacy.dataCollectionText":
      "نجمع المعلومات التي تقدمها عند إنشاء حساب، مثل اسمك وعنوان بريدك الإلكتروني ومعرف Instagram الاختياري. نجمع أيضًا توقعاتك وإحصائيات اللعبة.",
    "privacy.dataUse": "كيف نستخدم بياناتك",
    "privacy.dataUseText":
      "نستخدم بياناتك لتقديم خدمة التوقع وحساب النقاط والترتيبات وعرض لوحات المتصدرين وتحسين تجربة التطبيق.",
    "privacy.dataSharing": "مشاركة البيانات",
    "privacy.dataSharingText":
      "لا نبيع بياناتك الشخصية. قد تكون توقعاتك وإحصائياتك مرئية للمستخدمين الآخرين على لوحات المتصدرين وفي الفرق، لكن عنوان بريدك الإلكتروني يبقى خاصًا.",
    "privacy.dataSecurity": "أمان البيانات",
    "privacy.dataSecurityText":
      "نستخدم تدابير أمنية معيارية في الصناعة لحماية بياناتك. يتم تخزين جميع البيانات بأمان باستخدام Supabase.",
    "privacy.yourRights": "حقوقك",
    "privacy.yourRightsText":
      "لديك الحق في الوصول إلى حسابك وبياناتك أو تحديثها أو حذفها في أي وقت من خلال إعدادات التطبيق.",
    "privacy.changes": "تغييرات هذه السياسة",
    "privacy.changesText":
      "قد نحدث سياسة الخصوصية هذه من وقت لآخر. سنخطر المستخدمين بأي تغييرات كبيرة.",
    "privacy.developedBy": "تم تطوير هذا التطبيق بواسطة",

    // Contact Page
    "contact.title": "اتصل بنا",
    "contact.getInTouch": "تواصل معنا",
    "contact.description": "لديك سؤال أو اقتراح أو وجدت خطأ? نحب أن نسمع منك!",
    "contact.email": "بريدك الإلكتروني",
    "contact.emailPlaceholder": "you@example.com",
    "contact.message": "الرسالة",
    "contact.messagePlaceholder": "أخبرنا بما يدور في ذهنك...",
    "contact.send": "إرسال الرسالة",
    "contact.success": "تم إرسال الرسالة!",
    "contact.successMessage": "شكرًا لتواصلك معنا. سنعود إليك قريبًا.",
    "contact.alternative": "اتصال بديل",
    "contact.alternativeText": "يمكنك أيضًا الوصول إلينا مباشرة على:",
    "contact.developedBy": "تم تطوير هذا التطبيق بواسطة",

    // Time
    "time.today": "اليوم",
    "time.tomorrow": "غداً",
    "time.yesterday": "أمس",
    "time.sunday": "الأحد",
    "time.monday": "الإثنين",
    "time.tuesday": "الثلاثاء",
    "time.wednesday": "الأربعاء",
    "time.thursday": "الخميس",
    "time.friday": "الجمعة",
    "time.saturday": "السبت",
    "time.january": "يناير",
    "time.february": "فبراير",
    "time.march": "مارس",
    "time.april": "أبريل",
    "time.may": "مايو",
    "time.june": "يونيو",
    "time.july": "يوليو",
    "time.august": "أغسطس",
    "time.september": "سبتمبر",
    "time.october": "أكتوبر",
    "time.november": "نوفمبر",
    "time.december": "ديسمبر",
    "time.justNow": "الآن",
    "time.fewSecondsAgo": "منذ بضع ثوان",
    "time.minute": "دقيقة",
    "time.minutes": "دقائق",
    "time.hour": "ساعة",
    "time.hours": "ساعات",
    "time.day": "يوم",
    "time.days": "أيام",
    "time.ago": "منذ",
    "time.jan": "يناير",
    "time.feb": "فبراير",
    "time.mar": "مارس",
    "time.apr": "أبريل",
    "time.jun": "يونيو",
    "time.jul": "يوليو",
    "time.aug": "أغسطس",
    "time.sep": "سبتمبر",
    "time.oct": "أكتوبر",
    "time.nov": "نوفمبر",
    "time.dec": "ديسمبر",

    // Login Modal
    "login.title": "مرحباً بك مرة أخرى",
    "login.subtitle": "تسجيل الدخول إلى حسابك",
    "login.email": "البريد الإلكتروني",
    "login.password": "كلمة المرور",
    "login.name": "الاسم",
    "login.namePlaceholder": "أحمد محمد",
    "login.signIn": "تسجيل الدخول",
    "login.signUp": "إنشاء حساب",
    "login.noAccount": "ليس لديك حساب؟",
    "login.hasAccount": "لديك حساب بالفعل؟",
    "login.createAccount": "إنشاء حساب",
    "login.logout": "تسجيل الخروج",
    "login.profile": "الملف الشخصي",
    "login.instagram": "إنستغرام (اختياري)",
    "login.instagramHint": "سيتم عرض اسمك في قصصنا اليومية على إنستغرام!",
    "login.supportedCountry": "الفريق الذي تدعمه",
    "login.selectCountry": "اختر فريقك",

    // Common
    "common.loading": "جاري التحميل...",
    "common.saving": "جاري الحفظ...",
    "common.cancel": "إلغاء",
    "common.close": "إغلاق",
    "common.back": "رجوع",
    "common.noPredictions": "لا توجد توقعات بعد",
    "common.noPlayers": "لا يوجد لاعبون بعد",
    "common.signInToSave": "سجّل الدخول للحفظ",
    "common.predictionSaved": "تم حفظ التوقع",
    "common.noPredictionsMatch": "لا توجد توقعات بعد لهذه المباراة",
    "common.possessive": " لـ",
    "common.your": "توقعك",
    "common.nameRequired": "الاسم مطلوب",
    "common.failedCreateAccount": "فشل في إنشاء الحساب",
    "common.unexpectedError": "حدث خطأ غير متوقع",
    "common.loginRequired": "يجب تسجيل الدخول لإنشاء فريق",
    "common.leagueNameRequired": "اسم الفريق مطلوب",
    "common.failedCreateLeague": "فشل في إنشاء الفريق",
    "common.failedRenameLeague": "فشل في إعادة تسمية الفريق",
    "common.downloadFailed": "فشل التنزيل. يرجى المحاولة مرة أخرى.",
    "common.errorTitle": "حدث خطأ ما",
    "common.errorMessage": "نأسف، لكن حدث خطأ غير متوقع. يرجى تحديث الصفحة.",
    "common.refreshPage": "تحديث الصفحة",
    "common.error": "خطأ",
    "common.downloadSuccess": "تم تنزيل الصورة بنجاح!",
    "common.maybeLater": "ربما لاحقاً",
    "common.notNow": "ليس الآن",
    "common.change": "تغيير التوقع",
    "common.profileUpdated": "تم تحديث الملف الشخصي بنجاح!",

    // Login/Signup
    "login.success": "تم تسجيل الدخول بنجاح!",
    "login.signupSuccess": "تم إنشاء الحساب بنجاح!",

    // First Time Prediction Modal
    "firstTime.welcome": "مرحباً! 🎉",
    "firstTime.subtitle": "قم بتقديم توقعاتك الأولى وانضم إلى المنافسة!",
    "firstTime.whoWins": "من تعتقد أنه سيفوز؟",
    "firstTime.wins": "يفوز",
    "firstTime.draw": "تعادل",
    "firstTime.continue": "متابعة",
    "firstTime.yourPrediction": "توقعاتك",
    "firstTime.signUpToSave": "جاهز للانضمام؟ 🎉",
    "firstTime.signUpPrompt":
      "توقع رائع! أنشئ حساباً مجانياً لحفظه والمنافسة مع الآخرين. لن يستغرق الأمر سوى لحظة!",
    "firstTime.saveYourPrediction": "احفظ توقعاتك",
    "firstTime.signUpDescription":
      "أنشئ حساباً لحفظ توقعاتك وتتبع نقاطك والمنافسة في لوحة المتصدرين!",
    "firstTime.signUpNow": "سجل الآن",
    "firstTime.predictionSaved": "تم حفظ توقعاتك!",

    // First Prediction Info Modal
    "firstPrediction.title": "أكمل ملفك الشخصي",
    "firstPrediction.subtitle": "يرجى تقديم معلوماتك للمتابعة مع توقعك الأول",
    "firstPrediction.continue": "متابعة",

    // Squad Operations
    "squad.createSuccess": "تم إنشاء الفريق بنجاح!",
    "squad.renameSuccess": "تم إعادة تسمية الفريق بنجاح!",
    "squad.leaveSuccess": "تم مغادرة الفريق بنجاح!",

    // Admin Screen
    "admin.title": "المسؤول",
    "admin.panel": "لوحة المسؤول",
    "admin.matches": "المباريات",
    "admin.users": "المستخدمون",
    "admin.squads": "الفرق",
    "admin.allUsers": "جميع المستخدمين",
    "admin.allSquads": "جميع الفرق",
    "admin.noUsers": "لم يتم العثور على مستخدمين",
    "admin.noSquads": "لم يتم العثور على فرق",
    "admin.predictions": "التوقعات",
    "admin.members": "الأعضاء",
    "admin.registered": "تاريخ التسجيل",
    "admin.created": "تاريخ الإنشاء",
    "admin.createdBy": "أنشأه",
    "admin.squad": "الفريق",
    "admin.squadNotFound": "الفريق غير موجود",
    "admin.deleteSquad": "حذف الفريق",
    "admin.confirmDeleteSquad": "هل أنت متأكد أنك تريد حذف الفريق",
    "admin.deleteSquadWarning": "لا يمكن التراجع عن هذا الإجراء.",
    "admin.squadDeleted": "تم حذف الفريق بنجاح",
    "admin.failedToDeleteSquad": "فشل حذف الفريق",
    "admin.cannotDeleteSquadWithMembers": "لا يمكن حذف الفريق الذي يحتوي على أعضاء. قم بإزالة جميع الأعضاء أولاً.",
    "admin.activeMatches": "المباريات النشطة",
    "admin.noActiveMatches": "لا توجد مباريات نشطة",
    "admin.completedMatches": "المباريات المكتملة",
    "admin.noCompletedMatches": "لا توجد مباريات مكتملة بعد",
    "admin.matchUpdated": "تم تحديث المباراة بنجاح",
    "admin.matchCompleted": "اكتملت المباراة! تم منح النقاط وتحديث المراكز.",
    "admin.status.upcoming": "قادمة",
    "admin.status.live": "مباشر",
    "admin.status.completed": "مكتملة",
    "admin.save": "حفظ",
    "admin.saving": "جاري الحفظ...",
    "admin.errorDetails": "تفاصيل الخطأ",

    // Landing Screen
    "landing.noMatches": "لا توجد مباريات قادمة متاحة",

    // Squad
    "league.global": "عالمي",
    "league.myLeague": "فريقي",
    "league.create": "إنشاء فريق",
    "league.renameShare": "إعادة تسمية/مشاركة",
    "league.join": "الانضمام إلى فريق",
    "league.leave": "مغادرة الفريق",
    "league.code": "الكود",
    "league.members": "الأعضاء",
    "league.confirmLeave": "هل أنت متأكد أنك تريد مغادرة هذا الفريق؟",
    "league.namePlaceholder": "أدخل اسم الفريق",
    "league.codePlaceholder": "أدخل الكود المكون من 6 أرقام",

    // Podium Modal
    "podium.title": "🏆 المتصدرون الحاليون",
    "podium.subtitle": "شاهد من يقود المنافسة!",
    "podium.cta": "هل تريد الانضمام إلى المنصة؟",
    "podium.description":
      "قم بتقديم توقعات للمباريات القادمة وارتقِ في لوحة المتصدرين! قد يكون اسمك هنا أيضاً!",
    "podium.startPredicting": "ابدأ التوقع",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

interface LanguageProviderProps {
  children: React.ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(() => {
    // First, check if user has a saved preference
    const savedLanguage = localStorage.getItem("language") as Language;
    if (savedLanguage && ["en", "fr", "ar"].includes(savedLanguage)) {
      return savedLanguage;
    }

    // If no saved preference, detect from browser language
    const browserLang =
      navigator.language ||
      (navigator.languages && navigator.languages[0]) ||
      "en";
    const langCode = browserLang.toLowerCase().split("-")[0]; // Get base language code (e.g., "fr" from "fr-FR")

    // Map browser language to supported languages
    if (langCode === "fr") {
      return "fr";
    } else if (langCode === "ar") {
      return "ar";
    } else {
      // Default to English for "en" or any other language
      return "en";
    }
  });

  useEffect(() => {
    // Set RTL direction for Arabic, LTR for other languages
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("language", lang);
  }, []);

  const t = useCallback(
    (key: string): string => {
      const langTranslations = translations[language];
      return (langTranslations as Record<string, string>)[key] || key;
    },
    [language]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
    }),
    [language, setLanguage, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
